// eslint-disable-next-line import/named
import { DateSelectArg } from '@fullcalendar/core';
import {
  JxtAlert,
  JxtAlertTypeEnum,
  JxtLoader,
  JxtOffCanvasHeaderHideIconEnum,
  JxtQrCode,
  JxtResourceTabHeader,
  JxtTab,
} from '@jooxter/ui';
import {
  AddinBookingOffCanvasTabEnum,
  BookingOffCanvasTabEnum,
  createGTMGAEvent,
  fromISO,
  IQuickTimeSlot,
  isAddin,
  isSameTimezoneAsTheUser,
  now,
  OffCanvasModeEnum,
  toISO,
} from '@jooxter/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingQueryKeys, useFetchResource, useFetchResourcesTags, useFetchResourceTypes } from '../../queries';
import { useFetchBooking } from '@jooxter/bookings';
import { useOffCanvasStore } from '../../store';
import { Tab } from '@headlessui/react';
import { DateTime } from 'luxon';
import {
  JxtBookingDetailsContainer,
  JxtBookingFormContainer,
  JxtResourceCalendar,
  ResourceInformation,
} from '../../components';
import { JxtLocateResource } from '@jooxter/floor-plan';
import { useBookingToIJxtBookingDetailsAdapter } from '../../adapters';
import { useBookingPresenter } from '../../presenters';
import { useFullCalendarLocale } from '@jooxter/fullcalendar';
import { useBookingActions } from './useBookingActions';
import { getResourceType, useJxtRouter, queryClient, useTimezoneNameFormatter } from '../..';
import { AutoSelectFirstSlotContext } from './AutoSelectFirstSlotContext';
import { useFavoriteActions } from '../useFavoriteActions';
import { useLocation } from 'react-router';
import { ResourceTagCompressedTypeEnum } from '@jooxter/api';
import { getEnvVar } from '@jooxter/env';
import { isRtsm } from '../../routing/helpers';
import { IOffCanvasContent } from '../offCanvasContent.types';

// Reads the booking slice of the off-canvas store and turns it into the props JxtOffCanvas needs.
// Mounted once, at the off-canvas host - not once per page that can open a booking, which is what
// let two copies of this state machine race each other before (see ND-6258/ND-6129 history).
export const useBookingOffCanvasContent = (onBookingDelete?: (id: number) => void): IOffCanvasContent => {
  const booking = useOffCanvasStore((state) => state.booking);
  const {
    mode,
    bookingId,
    resourceId,
    preset,
    directEdit,
    autoSelectFirstSlot,
    slotToBookInstantly,
    selectedIndex,
    addinSelectedIndex,
    previousView,
    planningDate,
  } = booking;
  const {
    close,
    bookingGoToEditView,
    goToResourcePlan,
    onBookingEventClick: storeOnBookingEventClick,
    bookingGoBack,
    onBookingCalendarSelect,
    onBookingSlotSelect: storeOnBookingSlotSelect,
    setBookingMode,
    setBookingSelectedIndex,
    setAddinBookingSelectedIndex,
  } = useOffCanvasStore((state) => ({
    close: state.close,
    bookingGoToEditView: state.bookingGoToEditView,
    goToResourcePlan: state.goToResourcePlan,
    onBookingEventClick: state.onBookingEventClick,
    bookingGoBack: state.bookingGoBack,
    onBookingCalendarSelect: state.onBookingCalendarSelect,
    onBookingSlotSelect: state.onBookingSlotSelect,
    setBookingMode: state.setBookingMode,
    setBookingSelectedIndex: state.setBookingSelectedIndex,
    setAddinBookingSelectedIndex: state.setAddinBookingSelectedIndex,
  }));
  const location = useLocation();
  const { matchPath } = useJxtRouter();
  const isRtsmPath = isRtsm(location.pathname, matchPath);
  const { t, i18n } = useTranslation();
  const { resource } = useFetchResource(resourceId);
  const { booking: bookingInOffCanvas } = useFetchBooking(bookingId);
  const { bookingDetails } = useBookingToIJxtBookingDetailsAdapter(bookingInOffCanvas);
  const { presenter } = useBookingPresenter(bookingInOffCanvas);
  const { handleToggleFavorite } = useFavoriteActions();
  const { locale } = useFullCalendarLocale(i18n.language);
  const { cancel, deleteBookingModal } = useBookingActions(onBookingDelete);
  const { resourceTypes } = useFetchResourceTypes();
  const { tags } = useFetchResourcesTags(resourceId);
  const filteredTags = useMemo(
    () => tags?.filter((t) => t.type === ResourceTagCompressedTypeEnum.Tag).map((tag) => tag.hardwareIdentifier) ?? [],
    [tags]
  );

  const code = useMemo(
    () => (filteredTags.length > 0 ? <JxtQrCode value={`${getEnvVar('API_URL')}/r/${filteredTags[0]}`} /> : undefined),
    [filteredTags]
  );

  // The calendar unmounts when a booking is opened, so its current date is tracked here to be
  // handed to the store on click and restored on back.
  const currentPlanningDate = useRef<DateTime | undefined>(undefined);
  const { format: formatTimezoneName } = useTimezoneNameFormatter();
  const resourceCalendarProps = useMemo(() => {
    if (mode === OffCanvasModeEnum.Create) {
      if (resource) {
        return {
          resourceId: resource.id,
          resourceName: resource.name,
          resourceTypeName: resource.resourceType.name,
          resourceTypeId: resource.resourceType.id,
          capacity: resource.capacity,
          initialDate: planningDate
            ? fromISO(planningDate, resource.location.timezone)
            : fromISO(
                toISO(preset?.from?.setZone(resource.location.timezone, { keepLocalTime: true }) ?? now()),
                resource.location.timezone
              ),
          timezone: resource.location.timezone,
          isBookable: resource.bookable,
          shouldShowTimezoneWarning: !isSameTimezoneAsTheUser(resource.location.timezone),
          isFavorite: resource.favorite,
        };
      }
    } else if (mode === OffCanvasModeEnum.Update) {
      if (presenter.start && presenter.resource) {
        return {
          resourceId: presenter.resource.id,
          resourceName: presenter.resource.name,
          resourceTypeId: presenter.resource.resourceTypeId,
          resourceTypeName: getResourceType(presenter.resource.resourceTypeId, resourceTypes)?.name ?? '',
          capacity: presenter.resource.capacity,
          initialDate: fromISO(planningDate ?? presenter.start.dateTime, presenter.resource.timezone),
          timezone: presenter.resource.timezone,
          isBookable: presenter.resource.bookable,
          shouldShowTimezoneWarning: presenter.resource.timezone
            ? !isSameTimezoneAsTheUser(presenter.resource.timezone)
            : false,
          isFavorite: presenter.resource.favorite,
        };
      }
    }
  }, [mode, resource, preset?.from, planningDate, presenter.start, presenter.resource, resourceTypes]);

  useEffect(() => {
    if (!resourceCalendarProps?.isBookable) {
      if (isAddin()) {
        setAddinBookingSelectedIndex(AddinBookingOffCanvasTabEnum.Information);
      } else {
        setBookingSelectedIndex(BookingOffCanvasTabEnum.Information);
      }
    }
  }, [resourceCalendarProps, setAddinBookingSelectedIndex, setBookingSelectedIndex]);

  const bookingFormPreset = useMemo(() => {
    const resourcePayload = resource
      ? {
          ...resource,
          resourceTypeId: resource.resourceType.id,
          locationId: resource.location.id!,
          timezone: resource.location.timezone,
        }
      : undefined;

    if (!preset) {
      return { resource: resourcePayload };
    }

    // Snapshot of now() taken only when preset/resource change (booking form opening),
    // not on every render, so the user's input isn't fought by a continuous reset to now.
    const nowTime = now();
    const from = preset.from && preset.from > nowTime ? preset.from : nowTime;
    const to = preset.to ?? nowTime.plus({ hours: 1 });
    const isExpired = preset.to && preset.to < nowTime;

    return {
      from,
      to: isExpired ? from.plus({ hours: 1 }) : to,
      physicalAttendees: preset.physicalAttendees,
      organizerId: preset?.organizerId,
      rrule: preset.rrule,
      resource: resourcePayload,
    };
  }, [preset, resource]);

  const deleteBooking = useCallback(() => {
    if (bookingId && bookingDetails && cancel) {
      cancel({
        ...bookingDetails,
        id: bookingId,
        start: { dateTime: toISO(bookingDetails.date.range.from) },
        end: { dateTime: toISO(bookingDetails.date.range.to) },
        resourceName: bookingDetails.resource.name,
      });
    }
  }, [bookingId, bookingDetails, cancel]);

  const getIconList = useCallback((): { iconName: string; title: string; onClick: () => void }[] => {
    const icons: { iconName: string; title: string; onClick: () => void }[] = [];

    if (OffCanvasModeEnum.View !== mode || !bookingInOffCanvas) {
      return icons;
    }

    if (bookingInOffCanvas.capabilities?.update) {
      icons.push({
        iconName: 'pen',
        title: t<string>('update'),
        onClick: () => bookingGoToEditView(),
      });
    }

    if (bookingInOffCanvas.capabilities?.cancel) {
      icons.push({ iconName: 'trash', title: t<string>('delete'), onClick: () => deleteBooking() });
    }

    return icons;
  }, [mode, bookingInOffCanvas, t, bookingGoToEditView, deleteBooking]);

  const onEventClick = useCallback(
    (id: number) => {
      const date = currentPlanningDate.current;
      storeOnBookingEventClick(id, date ? toISO(date) : undefined);
    },
    [storeOnBookingEventClick]
  );

  const onSelection = useCallback(
    (selectionInfo: DateSelectArg) => {
      let from = DateTime.fromISO(selectionInfo.startStr, { zone: selectionInfo.view.getOption('timeZone') });
      const to = DateTime.fromISO(selectionInfo.endStr, { zone: selectionInfo.view.getOption('timeZone') });
      const nowSnapshot = now(selectionInfo.view.getOption('timeZone'));
      if (from < nowSnapshot) {
        from = nowSnapshot;
      }
      onBookingCalendarSelect({ from, to }, resourceCalendarProps?.resourceId);
    },
    [onBookingCalendarSelect, resourceCalendarProps?.resourceId]
  );

  const onBookSlot = useCallback(
    (slot: IQuickTimeSlot) => {
      storeOnBookingSlotSelect(
        slot,
        { ...preset, from: slot.start, to: slot.end, rrule: null },
        resourceCalendarProps?.resourceId
      );
    },
    [storeOnBookingSlotSelect, preset, resourceCalendarProps?.resourceId]
  );

  const onClickBookingTab = useCallback(() => {
    if (selectedIndex === BookingOffCanvasTabEnum.Information) {
      createGTMGAEvent('ResourceViewPanel', 'Booking', 'Open Creation Booking');
    }
  }, [selectedIndex]);

  const onHideWeb = useCallback(() => {
    if (mode === OffCanvasModeEnum.View && previousView) {
      bookingGoBack();
      return;
    }
    if (mode !== OffCanvasModeEnum.View) {
      if (selectedIndex === BookingOffCanvasTabEnum.Information) {
        createGTMGAEvent('ResourceViewPanel', 'Close', 'Close ResourceViewPanel');
      } else if (selectedIndex === BookingOffCanvasTabEnum.Form) {
        createGTMGAEvent('Booking', mode === OffCanvasModeEnum.Update ? 'Update Booking' : 'Create Booking', 'Cancel');
      }
      if (!directEdit) {
        setBookingMode(OffCanvasModeEnum.View);
        setBookingSelectedIndex(BookingOffCanvasTabEnum.Planning);
      } else {
        close();
      }
    } else {
      close();
    }
  }, [mode, previousView, selectedIndex, directEdit, bookingGoBack, setBookingMode, setBookingSelectedIndex, close]);

  const onHideAddin = useCallback(() => {
    if (mode !== OffCanvasModeEnum.View) {
      if (addinSelectedIndex === AddinBookingOffCanvasTabEnum.Information) {
        createGTMGAEvent('ResourceViewPanel', 'Close', 'Close ResourceViewPanel');
      }
      if (!directEdit) {
        setBookingMode(OffCanvasModeEnum.View);
      } else {
        close();
      }
    } else {
      close();
    }
  }, [mode, addinSelectedIndex, directEdit, setBookingMode, close]);

  const webConfiguration = useMemo(
    () => ({
      header: {
        hideIcon:
          !(mode === OffCanvasModeEnum.View && previousView) && (mode === OffCanvasModeEnum.View || directEdit)
            ? JxtOffCanvasHeaderHideIconEnum.Close
            : JxtOffCanvasHeaderHideIconEnum.Back,
        iconList: getIconList(),
        title: mode !== OffCanvasModeEnum.View && !!resourceCalendarProps && (
          <JxtResourceTabHeader
            name={resourceCalendarProps.resourceName}
            id={resource?.id || resourceCalendarProps.resourceId}
            type={resourceCalendarProps.resourceTypeName}
            capacity={resourceCalendarProps.capacity}
            isFavorite={resource?.favorite ?? resourceCalendarProps.isFavorite}
            onToggleFavorite={(favorite: boolean, id: number, tagCategory: string) =>
              handleToggleFavorite(favorite, id, tagCategory).then(() =>
                queryClient.invalidateQueries({ queryKey: [BookingQueryKeys.GetBooking, bookingId] })
              )
            }
          />
        ),
        children: mode !== OffCanvasModeEnum.View && (
          <>
            {resourceCalendarProps?.shouldShowTimezoneWarning && resourceCalendarProps?.timezone && (
              <JxtAlert
                text={formatTimezoneName(resourceCalendarProps.timezone)}
                type={JxtAlertTypeEnum.Warning}
                closeable={false}
              />
            )}
            <Tab.List className="flex justify-around gap-2">
              <JxtTab
                text={t('navbar.scheduler')}
                icon="calendar"
                disabled={!resourceCalendarProps?.isBookable}
                active={selectedIndex === BookingOffCanvasTabEnum.Planning}
                onClick={() => setBookingMode(bookingId ? OffCanvasModeEnum.Update : OffCanvasModeEnum.Create)}
              />
              <JxtTab
                text={t('information')}
                icon="info-circle"
                active={selectedIndex === BookingOffCanvasTabEnum.Information}
                onClick={() => {
                  setBookingSelectedIndex(BookingOffCanvasTabEnum.Information);
                  setBookingMode(bookingId ? OffCanvasModeEnum.Update : OffCanvasModeEnum.Create);
                }}
              />
              {presenter.shouldDisplayBookingTab() && (
                <JxtTab
                  text={t('booking-button')}
                  icon="calendar-plus"
                  disabled={!resourceCalendarProps?.isBookable}
                  active={selectedIndex === BookingOffCanvasTabEnum.Form}
                  onClick={onClickBookingTab}
                />
              )}
            </Tab.List>
          </>
        ),
      },
      onHide: onHideWeb,
      bodyClass: 'overflow-y-auto',
      children: (
        <>
          {mode === OffCanvasModeEnum.View && !!bookingDetails && bookingId && (
            <JxtBookingDetailsContainer bookingDetails={bookingDetails} id={bookingId} />
          )}
          {mode !== OffCanvasModeEnum.View && (
            <div className="flex flex-col h-full">
              <Tab.Panels className="grow flex min-h-0">
                <Tab.Panel className="grow flex">
                  {resourceCalendarProps?.isBookable && (
                    <JxtResourceCalendar
                      {...resourceCalendarProps}
                      locale={locale}
                      onSelect={onSelection}
                      onEventClick={onEventClick}
                      onDateChange={(date) => {
                        currentPlanningDate.current = date;
                      }}
                      bookSlot={onBookSlot}
                    />
                  )}
                </Tab.Panel>
                <Tab.Panel className="h-full grow min-w-0">
                  {bookingInOffCanvas?.resource.id && (
                    <ResourceInformation resourceId={bookingInOffCanvas?.resource.id} />
                  )}
                  {resource?.id && <ResourceInformation resourceId={resource?.id} />}
                </Tab.Panel>
                <Tab.Panel className="h-full grow min-w-0" unmount={true}>
                  {bookingInOffCanvas && mode === OffCanvasModeEnum.Update && (
                    <JxtBookingFormContainer bookingToEdit={bookingInOffCanvas} />
                  )}
                  {resource?.id && resource.bookable && mode === OffCanvasModeEnum.Create && (
                    <AutoSelectFirstSlotContext.Provider value={autoSelectFirstSlot}>
                      <JxtBookingFormContainer
                        bookingFormPreset={bookingFormPreset}
                        slotToBookInstantly={slotToBookInstantly}
                      />
                    </AutoSelectFirstSlotContext.Provider>
                  )}
                </Tab.Panel>
              </Tab.Panels>
              {code && isRtsmPath && (
                <div className="shrink-0 h-32 flex justify-center items-center border-t border-neutral-20 pt-3 gap-4">
                  {code}
                  <span className="text-neutral-60 flex items-center justify-center text-wrap w-36 text-center">
                    {t('rtsm.qr.code.scan')}
                  </span>
                </div>
              )}
            </div>
          )}
          {!(bookingDetails || resourceCalendarProps) && (
            <div className="absolute top-0 right-0 left-0 bottom-0 backdrop-blur-sm h-full flex flex-1 items-center justify-center">
              <JxtLoader />
            </div>
          )}
          {deleteBookingModal}
        </>
      ),
      tabConfiguration: {
        selectedIndex,
        defaultIndex: selectedIndex,
        onChange: (index: BookingOffCanvasTabEnum) => setBookingSelectedIndex(index),
      },
    }),
    [
      resourceCalendarProps,
      mode,
      directEdit,
      previousView,
      selectedIndex,
      bookingDetails,
      bookingId,
      resource,
      deleteBookingModal,
      autoSelectFirstSlot,
      slotToBookInstantly,
      bookingFormPreset,
      bookingInOffCanvas,
      handleToggleFavorite,
      presenter,
      locale,
      code,
      isRtsmPath,
      formatTimezoneName,
      t,
      getIconList,
      onSelection,
      onEventClick,
      onBookSlot,
      onClickBookingTab,
      onHideWeb,
      setBookingMode,
      setBookingSelectedIndex,
    ]
  );

  const addinConfiguration = useMemo(
    () => ({
      header: {
        hideIcon:
          mode === OffCanvasModeEnum.View || directEdit
            ? JxtOffCanvasHeaderHideIconEnum.Close
            : JxtOffCanvasHeaderHideIconEnum.Back,
        iconList: getIconList(),
        title: mode !== OffCanvasModeEnum.View && !!resourceCalendarProps && (
          <JxtResourceTabHeader
            name={resourceCalendarProps.resourceName}
            id={resource?.id || resourceCalendarProps.resourceId}
            type={resourceCalendarProps.resourceTypeName}
            capacity={resourceCalendarProps.capacity}
            isFavorite={resource?.favorite ?? resourceCalendarProps.isFavorite}
            onToggleFavorite={(favorite: boolean, id: number, tagCategory: string) =>
              handleToggleFavorite(favorite, id, tagCategory).then(() =>
                queryClient.invalidateQueries({ queryKey: [BookingQueryKeys.GetBooking, bookingId] })
              )
            }
          />
        ),
        children: mode !== OffCanvasModeEnum.View && (
          <>
            {resourceCalendarProps?.shouldShowTimezoneWarning && resourceCalendarProps?.timezone && (
              <JxtAlert
                text={formatTimezoneName(resourceCalendarProps.timezone)}
                type={JxtAlertTypeEnum.Warning}
                closeable={false}
              />
            )}
            <Tab.List className="flex justify-around gap-2">
              <JxtTab
                text={t('information')}
                icon="info-circle"
                active={addinSelectedIndex === AddinBookingOffCanvasTabEnum.Information}
                onClick={() => {
                  setAddinBookingSelectedIndex(AddinBookingOffCanvasTabEnum.Information);
                  setBookingMode(bookingId ? OffCanvasModeEnum.Update : OffCanvasModeEnum.Create);
                }}
              />
              <JxtTab
                text={'Plan'}
                icon="location-dot"
                active={addinSelectedIndex === AddinBookingOffCanvasTabEnum.Plan}
                onClick={() => {
                  createGTMGAEvent('Spaces', 'Resources', 'Localized');
                  goToResourcePlan();
                }}
              />
            </Tab.List>
          </>
        ),
      },
      onHide: onHideAddin,
      bodyClass: 'overflow-y-auto',
      children: (
        <>
          {mode === OffCanvasModeEnum.View && !!bookingDetails && bookingId && (
            <JxtBookingDetailsContainer bookingDetails={bookingDetails} id={bookingId} />
          )}
          {mode !== OffCanvasModeEnum.View && (
            <div className="flex flex-col h-full w-full min-h-0 pt-4">
              <Tab.Panels className="flex-1 overflow-auto">
                <Tab.Panel className="h-full px-4">
                  {bookingInOffCanvas?.resource.id && (
                    <ResourceInformation resourceId={bookingInOffCanvas?.resource.id} />
                  )}
                  {resource?.id && <ResourceInformation resourceId={resource?.id} />}
                </Tab.Panel>
                <Tab.Panel className="h-full">
                  {resource?.id && <JxtLocateResource resourceId={resource.id} width={300} height={300} />}
                </Tab.Panel>
              </Tab.Panels>
            </div>
          )}
          {!resourceCalendarProps && (
            <div className="absolute top-0 right-0 left-0 bottom-0 backdrop-blur-sm h-full flex flex-1 items-center justify-center">
              <JxtLoader />
            </div>
          )}
        </>
      ),
      tabConfiguration: {
        selectedIndex: addinSelectedIndex,
        defaultIndex: addinSelectedIndex,
        onChange: (index: AddinBookingOffCanvasTabEnum) => setAddinBookingSelectedIndex(index),
      },
    }),
    [
      mode,
      directEdit,
      resourceCalendarProps,
      resource,
      formatTimezoneName,
      t,
      addinSelectedIndex,
      bookingDetails,
      bookingId,
      bookingInOffCanvas,
      handleToggleFavorite,
      getIconList,
      goToResourcePlan,
      onHideAddin,
      setAddinBookingSelectedIndex,
      setBookingMode,
    ]
  );

  return isAddin() ? addinConfiguration : webConfiguration;
};
